import React, { FC, useMemo } from 'react';
import styles from './index.module.scss';
import { Progress, Spin } from 'antd';
import { useRequest } from 'ahooks';
import dayjs, { Dayjs } from 'dayjs';
import { apiV2PaintingDashboardEnergyCostPost } from '@maxtropy/device-customer-apis-v2';
import { keepOneAndWanOrLine, keepOneOrLine } from '@/utils/utils';
import { IndexTimeActive } from '../../SwitchDate';
import iconEnergyTotal from '../../../imgs/icon_energy_total.png';
import iconPlaceholder from '../../../imgs/icon_placeholder.png';
import iconElectricity from '../../../imgs/icon_electricity.png';
import iconElectricityWorkshop from '../../../imgs/icon_electricity_workshop.png';
import iconNaturalGasWorkshop from '../../../imgs/icon_natural_gas_workshop.png';
import bannerHeader from '../../../imgs/banner_header.png';
import { Empty } from '@maxtropy/components';

/** 能源类型枚举 */
enum EnergyType {
  /** 电 */
  ELECTRICITY = 'ELECTRICITY',
  /** 压缩空气 */
  AIR = 'AIR',
  /** 天然气 */
  GAS = 'GAS',
}

/** 能源类型映射：API 返回值 → 中文标签 */
const ENERGY_TYPE_LABELS: Record<EnergyType, string> = {
  [EnergyType.ELECTRICITY]: '电',
  [EnergyType.AIR]: '压缩空气',
  [EnergyType.GAS]: '天然气',
};

/** 能源类型单位映射 */
const ENERGY_TYPE_UNITS: Record<EnergyType, string> = {
  [EnergyType.ELECTRICITY]: 'kWh',
  [EnergyType.AIR]: 'm³',
  [EnergyType.GAS]: 'm³',
};

/** 能源类型图标映射：中文标签 → 图标 */
const ENERGY_TYPE_ICONS: Record<string, string> = {
  电: iconElectricity,
  压缩空气: iconElectricityWorkshop,
  天然气: iconNaturalGasWorkshop,
};

interface EnergyStatsProps {
  /** 时间粒度：月/年 */
  activeKey: IndexTimeActive;
  /** 选定时间 */
  selectedDate: Dayjs;
}

const EnergyStats: FC<EnergyStatsProps> = ({ activeKey, selectedDate }) => {
  // 构建请求参数
  const requestParams = useMemo(() => {
    const timeResolution = activeKey === IndexTimeActive.THIS_MONTH ? 'M' : 'Y';
    const unit = activeKey === IndexTimeActive.THIS_MONTH ? 'month' : 'year';
    const startTime = selectedDate.startOf(unit).valueOf();
    // 结束时间：前一天23:59:59（如果是当前月份/年份），否则为月末/年末
    const isCurrentPeriod =
      selectedDate.format(activeKey === IndexTimeActive.THIS_MONTH ? 'YYYY-MM' : 'YYYY') ===
      dayjs().format(activeKey === IndexTimeActive.THIS_MONTH ? 'YYYY-MM' : 'YYYY');
    const endTime = isCurrentPeriod
      ? dayjs().subtract(1, 'day').endOf('day').valueOf()
      : selectedDate.endOf(unit).valueOf();
    return { startTime, endTime, timeResolution };
  }, [activeKey, selectedDate]);

  const { data, loading } = useRequest(() => apiV2PaintingDashboardEnergyCostPost(requestParams), {
    refreshDeps: [requestParams],
  });

  const energyStats = useMemo(() => {
    if (!data) return null;
    return {
      totalCost: keepOneAndWanOrLine(data.totalActualCost),
      plannedCost: keepOneAndWanOrLine(data.totalPlanCost),
      completionRate: keepOneOrLine(data.completionRate),
      originCompletionRate: data.completionRate,
    };
  }, [data]);

  const energyCostTable = useMemo(() => {
    if (!data?.costItems?.length) return [];
    return data.costItems.map(item => {
      const energyType = item.energyType as EnergyType;
      const typeLabel = ENERGY_TYPE_LABELS[energyType] ?? '--';
      return {
        type: typeLabel,
        icon: ENERGY_TYPE_ICONS[typeLabel] ?? '',
        usage: keepOneAndWanOrLine(item.usage),
        usageUnit: ENERGY_TYPE_UNITS[energyType] ?? '',
        actualCost: keepOneAndWanOrLine(item.actualCost),
        planCost: keepOneAndWanOrLine(item.planCost),
        ratio: keepOneOrLine(item.ratio),
        originRatio: item.ratio,
      };
    });
  }, [data]);

  if (loading) {
    return (
      <div className={styles.loading_wrapper}>
        <Spin />
      </div>
    );
  }

  if (!energyStats) {
    return <Empty />;
  }

  return (
    <>
      <div className={styles.energy_stats_card}>
        <div className={styles.energy_stats_top} style={{ backgroundImage: `url(${bannerHeader})` }}>
          <div className={styles.energy_stats_left}>
            <div className={styles.energy_icon_wrapper}>
              <div className={styles.energy_icon_ring}>
                <img className={styles.energy_icon} src={iconEnergyTotal} alt="能源总成本" />
              </div>
              <img className={styles.energy_icon_placeholder} src={iconPlaceholder} alt="" />
            </div>
            <div className={styles.energy_data_group}>
              <div className={styles.energy_data_item}>
                <span className={styles.energy_data_label}>能源总成本(元)</span>
                <span className={styles.energy_data_value}>{energyStats.totalCost}</span>
              </div>
              <div className={styles.energy_data_item}>
                <span className={styles.energy_data_label}>计划成本(元)</span>
                <span className={styles.energy_data_value_plan}>{energyStats.plannedCost}</span>
              </div>
            </div>
          </div>
          <div className={styles.energy_stats_right}>
            <div className={styles.completion_rate_header}>
              <span className={styles.completion_rate_label}>完成率：</span>
              <span className={styles.completion_rate_value}>{energyStats.completionRate}%</span>
            </div>
            <Progress
              percent={typeof energyStats.originCompletionRate === 'number' ? energyStats.originCompletionRate : 0}
              showInfo={false}
              strokeColor={{ from: '#2D8DFF', to: '#52E7FF' }}
              trailColor="rgba(45, 141, 226, 0.2)"
              size={[200, 8]}
            />
          </div>
        </div>
      </div>
      <div className={styles.cost_table_wrapper}>
        <div className={styles.cost_table_header}>
          <span>能源类型</span>
          <span>用量</span>
          <span>实际成本(元)</span>
          <span>计划成本(元)</span>
          <span>完成率</span>
        </div>
        <div className={styles.cost_table}>
          {energyCostTable.length === 0 ? (
            <Empty description="暂无数据" />
          ) : (
            energyCostTable.map((item, idx) => (
              <div
                key={idx}
                className={`${styles.cost_table_row} ${item.type === '压缩空气' ? styles.row_compressed_air : ''}`}
              >
                <span className={styles.cost_type}>
                  <img className={styles.cost_icon_img} src={item.icon} alt={item.type} />
                  {item.type}
                </span>
                <span className={styles.cost_usage}>
                  {item.usage}
                  {item.usage !== '--' && item.usageUnit ? ` ${item.usageUnit}` : ''}
                </span>
                <span className={styles.cost_value}>{item.actualCost}</span>
                <span className={styles.cost_value_plan}>{item.planCost}</span>
                <div className={styles.cost_rate_cell}>
                  <Progress
                    type="circle"
                    percent={typeof item.originRatio === 'number' ? item.originRatio : 0}
                    showInfo={false}
                    strokeColor="#16DD8E"
                    trailColor="rgba(255, 255, 255, 0.1)"
                    size={32}
                    strokeWidth={16}
                  />
                  <span className={styles.cost_rate_text}>{item.ratio}%</span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
};

export default EnergyStats;
